import { intersectionBy } from "lodash-es";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type InjectedFormProps, SubmissionError, formValueSelector, reduxForm } from "redux-form";

import {
  ActionButton,
  ActionButtonIntent,
  ActionButtonSize,
  DialogComponent,
  HStack,
  InfoToggletip,
  Modal,
} from "widgets";
import { useOverlay } from "widgets/hooks";

import type WebApiClient from "@/api/WebApiClient.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { simpleDropdownMultiSelectFieldValidation, simpleFieldValidation } from "@/app-utils/form.ts";
import type { InputVirtualGenomeInput, Organism, User } from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import { searchSequencedGenomes } from "@/redux/modules/genome-browser.ts";
import { loadAccessibleUserGroups } from "@/redux/modules/listing.ts";
import { checkGenomeName } from "@/redux/modules/ncbi-admin.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { MAX_LENGTH } from "@/settings/settings.ts";
import { SelForm } from "@/ui-modules/core/form/layout/Form.tsx";
import InputField, { InputReduxFormField } from "@/ui-modules/core/redux-form/InputField.tsx";
import ReactSelectField, { ReactSelectReduxFormField } from "@/ui-modules/core/redux-form/ReactSelectField.tsx";
import {
  VIRTUAL_GENOME_CREATION_FORM_KEYS,
  VIRTUAL_GENOME_CREATION_FORM_NAME,
} from "@/ui-modules/entities/genomes/VirtualGenomeCreationDialogSettings.ts";
import {
  type UserGroupFragment,
  useCreateVirtualGenomeMutation,
  useGetCombinantGenomesUsergroupsLazyQuery,
} from "@/ui-modules/entities/genomes/graphql/genomes.gql.ts";
import type { OrganismWithUsergroups } from "@/ui-modules/entities/genomes/utils/types.ts";
import { FieldTitle } from "@/ui-modules/playbooks/FieldTitle.tsx";

interface VirtualGenomeCreationDialogProps {
  onClose: (virtualGenome?: Organism | null) => void;
}

interface VirtualGenomeCreationDialogFormProps
  extends VirtualGenomeCreationDialogProps,
    InjectedFormProps<FormData, ReduxDispatch> {
  isOpen: boolean;
}

const VirtualGenomeCreationDialog: FC<VirtualGenomeCreationDialogFormProps> = (props) => {
  const { overlayProps, state } = useOverlay({
    isOpen: props.isOpen,
    onOpenChange(isOpen) {
      if (!isOpen) props.onClose();
    },
  });

  const dispatch = useDispatch<ReduxDispatch>();
  const user: User = useSelector<IAppState, User>((state) => state.auth.session);

  useEffect(() => {
    void dispatch(loadAccessibleUserGroups());
  }, []);

  const { selectedGenomes, selectedUserGroups } = useSelector<
    IAppState,
    { selectedGenomes: Organism[]; selectedUserGroups: UserGroupFragment[] }
  >((state) => {
    const valueSelector = formValueSelector(VIRTUAL_GENOME_CREATION_FORM_NAME);
    const selectedGenomes: Organism[] = valueSelector(state, VIRTUAL_GENOME_CREATION_FORM_KEYS.COMBINANT_GENOMES);
    const selectedUserGroups: UserGroupFragment[] = valueSelector(state, VIRTUAL_GENOME_CREATION_FORM_KEYS.USER_GROUPS);
    return {
      selectedGenomes,
      selectedUserGroups,
    };
  });

  const [matchingUsergoups, setMatchingUsergroups] = useState<UserGroupFragment[]>([]);
  const [isLoadingUserGroups, setLoadingUserGroups] = useState<boolean>(false);

  const [getUserGroups] = useGetCombinantGenomesUsergroupsLazyQuery();

  // fetch the user groups of every genome to figure out what user groups overlap and could match the target virtual genome
  useEffect(() => {
    if (!selectedGenomes?.length) {
      setMatchingUsergroups([]);
      return;
    }

    setLoadingUserGroups(true);

    void Promise.all(
      selectedGenomes.map(async (genome) => {
        const { data } = await getUserGroups({ variables: { organismId: genome.id } });
        return data.organism;
      }),
    ).then((genomesWithUserGroups: OrganismWithUsergroups[]) => {
      let matchingUsergroups: UserGroupFragment[] = null;

      for (const organism of genomesWithUserGroups) {
        if (matchingUsergroups == null) {
          matchingUsergroups = [...organism.userGroups];
        } else {
          const matching = intersectionBy(matchingUsergroups, organism.userGroups, (userGroup) => userGroup.id);
          matchingUsergroups = [...matching];
        }
      }

      // adapt the current selection in case some selected userGroup options do not match any more
      if (selectedUserGroups?.length) {
        const matchingSelectedUserGroups = intersectionBy(
          selectedUserGroups,
          matchingUsergroups,
          (userGroup) => userGroup.id,
        );
        props.change(VIRTUAL_GENOME_CREATION_FORM_KEYS.USER_GROUPS, matchingSelectedUserGroups);
      }
      setMatchingUsergroups(matchingUsergroups);
      setLoadingUserGroups(false);
    });
  }, [selectedGenomes]);

  const mismatchingError = useMemo(() => {
    return selectedGenomes?.length && !matchingUsergoups.length && !isLoadingUserGroups;
  }, [selectedGenomes, matchingUsergoups, isLoadingUserGroups]);

  const [createVirtualGenome] = useCreateVirtualGenomeMutation();

  const handleSubmitData = useCallback(
    (values, _dispatch, props: VirtualGenomeCreationDialogFormProps) => {
      const genomeName: string = values[VIRTUAL_GENOME_CREATION_FORM_KEYS.GENOME_NAME];
      const genomeDescription: string = values[VIRTUAL_GENOME_CREATION_FORM_KEYS.GENOME_DESCRIPTION];
      // if only the Public user group is available to the user then the input field will be hidden
      const selectedUserGroups: UserGroupFragment[] =
        user.userGroups.length > 1 ? values[VIRTUAL_GENOME_CREATION_FORM_KEYS.USER_GROUPS] : user.userGroups;
      const genomes: Organism[] = values[VIRTUAL_GENOME_CREATION_FORM_KEYS.COMBINANT_GENOMES];

      const virtualGenomeInput = {
        genomeName,
        genomeDescription,
        componentOrgIds: genomes.map((g) => g.id),
        userGroupIds: selectedUserGroups.map((ug) => ug.id),
      } satisfies InputVirtualGenomeInput;

      return createVirtualGenome({ variables: { virtualGenomeInput: virtualGenomeInput } }).then(
        (result) => {
          props.onClose(result.data?.createVirtualGenome);
        },
        (rejected) => {
          throw new SubmissionError({ _error: rejected.message });
        },
      );
    },
    [props.onClose, matchingUsergoups],
  );

  return (
    <>
      <Modal state={state} {...overlayProps}>
        <DialogComponent
          title={"Create a New Virtual Genome"}
          icon={"add"}
          mainButton={{
            label: "Create",
            onPress: props.handleSubmit(handleSubmitData),
            isLoading: !!props.submitting,
          }}
          errorCallout={{ message: mismatchingError ? "The user groups of the genomes need to overlap" : props.error }}
          closeFn={state.close}
          contentStyle={{ width: 600 }}
        >
          <SelForm>
            <InputReduxFormField
              componentClass="input"
              name={VIRTUAL_GENOME_CREATION_FORM_KEYS.GENOME_NAME}
              label={<FieldTitle label={"Genome name"} mandatory />}
              placeholder={selMessage({ id: "form.input.placeholder.mandatory" })}
              validate={[simpleFieldValidation]}
              maxLength={MAX_LENGTH.GENOME.NAME}
              component={InputField}
            />

            <InputReduxFormField
              componentClass="textarea"
              name={VIRTUAL_GENOME_CREATION_FORM_KEYS.GENOME_DESCRIPTION}
              label={<FieldTitle label={"Genome description"} />}
              placeholder={selMessage({ id: "form.input.placeholder.mandatory" })}
              maxLength={MAX_LENGTH.GENOME.DESCRIPTION}
              component={InputField}
              rows={2}
            />

            {/* could use SequencedGenomeSelector if we add a "withoutVirtualGenomes" flag */}
            <ReactSelectReduxFormField
              name={VIRTUAL_GENOME_CREATION_FORM_KEYS.COMBINANT_GENOMES}
              component={ReactSelectField}
              label={<FieldTitle label={"Component genomes"} mandatory />}
              isMulti={true}
              async={true}
              loadOptions={(searchTerm: string) =>
                dispatch(searchSequencedGenomes(searchTerm, true)).then((search) => search.genomes)
              }
              formatOptionLabel={(genome: Organism) => `${genome.name}${genome.species && ` (${genome.species.name})`}`}
              getOptionValue={(genome: Organism) => String(genome.id)}
              placeholder={"Select component genomes..."}
              noOptionsMessage={() => "No matching genome..."}
              cacheOptions={true}
              isSearchable
              isClearable={true}
              defaultOptions={true} // tells the control to immediately fire the remote request
              validate={simpleDropdownMultiSelectFieldValidation}
            />

            {user.userGroups.length > 1 && (
              <ReactSelectReduxFormField
                name={VIRTUAL_GENOME_CREATION_FORM_KEYS.USER_GROUPS}
                label={
                  <FieldTitle
                    label={"Visibility"}
                    mandatory
                    rightElement={
                      <InfoToggletip
                        content={"Only the user groups shared by all of the component genomes will be selectable."}
                      />
                    }
                  />
                }
                options={matchingUsergoups}
                placeholder={"Select matching user groups"}
                getOptionValue={(option: UserGroupFragment) => String(option.id)}
                getOptionLabel={(option: UserGroupFragment) => option.name}
                validate={simpleDropdownMultiSelectFieldValidation}
                isLoading={isLoadingUserGroups}
                creatable={false}
                component={ReactSelectField}
                isMulti={true}
              />
            )}
          </SelForm>
        </DialogComponent>
      </Modal>
    </>
  );
};

const asyncValidate = async (
  values: FormData,
  dispatch: ReduxDispatch,
  props: VirtualGenomeCreationDialogFormProps,
) => {
  if (values == null) {
    return;
  }

  // uniqueness check of the genome name
  const genomeName: string = values[VIRTUAL_GENOME_CREATION_FORM_KEYS.GENOME_NAME];
  if (genomeName) {
    const nameValidationMessage = await dispatch(checkGenomeName(genomeName));
    if (nameValidationMessage) {
      throw {
        [VIRTUAL_GENOME_CREATION_FORM_KEYS.GENOME_NAME]: nameValidationMessage,
      };
    }
  }

  // check if the combination already exists
  const combinantGenomes: Organism[] = values[VIRTUAL_GENOME_CREATION_FORM_KEYS.COMBINANT_GENOMES];
  if (combinantGenomes?.length === 1) {
    throw {
      [VIRTUAL_GENOME_CREATION_FORM_KEYS.COMBINANT_GENOMES]:
        "You are not allowed to create a virtual genome with only a single genome component. Please select this genome directly.",
    };
  }

  if (combinantGenomes?.length > 1) {
    const existingVirtualGenomes: Organism[] = await dispatch(
      checkVirtualGenomeExists(combinantGenomes.map((genome) => genome.id)),
    );
    if (existingVirtualGenomes.length === 1) {
      throw {
        [VIRTUAL_GENOME_CREATION_FORM_KEYS.COMBINANT_GENOMES]: (
          <HStack>
            This virtual genome already exists:
            <ActionButton
              icon={"swap_horiz"}
              size={ActionButtonSize.Small}
              label={`Use ${existingVirtualGenomes[0].name}`}
              intent={ActionButtonIntent.Outlined}
              onPress={() => props.onClose(existingVirtualGenomes[0])}
            />
          </HStack>
        ),
      };
    }

    if (existingVirtualGenomes.length > 1) {
      throw {
        [VIRTUAL_GENOME_CREATION_FORM_KEYS.COMBINANT_GENOMES]: `This virtual genome already exists multiple times: ${existingVirtualGenomes.map((genome) => genome.name).join(", ")}. Please contact your administrator.`,
      };
    }
  }
};

export function checkVirtualGenomeExists(genomeIds: number[]) {
  return (dispatch: ReduxDispatch, _getState, webApiClient: WebApiClient): Promise<Organism[]> => {
    return webApiClient.checkVirtualGenomeExists(genomeIds).catch(
      createErrorHandler(dispatch, {
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export const VirtualGenomeCreationDialogForm = reduxForm({
  form: VIRTUAL_GENOME_CREATION_FORM_NAME,
  asyncValidate: asyncValidate,
  asyncBlurFields: [VIRTUAL_GENOME_CREATION_FORM_KEYS.GENOME_NAME, VIRTUAL_GENOME_CREATION_FORM_KEYS.COMBINANT_GENOMES], // so that only blurring this field triggers the async validation
})(VirtualGenomeCreationDialog) as any;
